import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { 
  Plus, Trash2, Edit2, Newspaper, Image as ImageIcon, CheckCircle, XCircle, FileText, Settings2, Globe, Sparkles, Loader2
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SpotNews, SpotNewsStatus, SpotNewsConfig, getSpotNewsData, addSpotNews, updateSpotNews, deleteSpotNews, saveSpotNewsConfig } from "@/data/spotNewsStore";

const AdminSpotNewsPage = () => {
  const { toast } = useToast();
  
  const [newsList, setNewsList] = useState<SpotNews[]>([]);
  const [config, setConfig] = useState<SpotNewsConfig>({ isVisible: true, bannerTitle: "Skip pas!", speed: 60 });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  
  const initialForm: Omit<SpotNews, "id" | "status" | "createdAt" | "updatedAt"> = {
    title: "",
    titleAr: "",
    titleZgh: "",
    content: "",
    contentAr: "",
    contentZgh: "",
    imageUrl: "",
    linkUrl: "",
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    const loadData = async () => {
      const { news, config } = await getSpotNewsData();
      setNewsList(news);
      setConfig(config);
    };
    
    loadData();

    const handleUpdate = () => {
      loadData();
    };
    window.addEventListener("kaynin_spot_news_updated", handleUpdate);
    return () => window.removeEventListener("kaynin_spot_news_updated", handleUpdate);
  }, []);

  const handleSaveConfig = async () => {
    await saveSpotNewsConfig(config);
    toast({ title: "Configuration sauvegardée", description: "Les paramètres du bandeau ont été mis à jour." });
  };

  const handleSave = async (status: SpotNewsStatus) => {
    if (!formData.title || !formData.content) {
      toast({ title: "Erreur", description: "Veuillez fournir un titre et un contenu.", variant: "destructive" });
      return;
    }

    if (editingId) {
      await updateSpotNews(editingId, { ...formData, status });
      toast({ title: "Modifiée", description: `L'actualité a été modifiée en tant que ${status === 'published' ? 'publiée' : 'brouillon'}.` });
    } else {
      await addSpotNews({ ...formData, status });
      toast({ title: "Créée", description: `L'actualité a été créée en tant que ${status === 'published' ? 'publiée' : 'brouillon'}.` });
    }
    
    setShowForm(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  const handleDelete = async (id: string) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cette actualité ?")) {
      await deleteSpotNews(id);
      toast({ title: "Supprimée", description: "L'actualité a été supprimée." });
    }
  };

  const handleEdit = (news: SpotNews) => {
    setFormData({
      title: news.title,
      titleAr: news.titleAr || "",
      titleZgh: news.titleZgh || "",
      content: news.content,
      contentAr: news.contentAr || "",
      contentZgh: news.contentZgh || "",
      imageUrl: news.imageUrl,
      linkUrl: news.linkUrl,
    });
    setEditingId(news.id);
    setShowForm(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({ ...formData, imageUrl: event.target?.result as string });
    };
    reader.readAsDataURL(file);
  };

  const toggleStatus = async (id: string, currentStatus: SpotNewsStatus) => {
    const newStatus = currentStatus === "published" ? "draft" : "published";
    await updateSpotNews(id, { status: newStatus });
    toast({ title: "Statut mis à jour", description: `L'actualité est maintenant ${newStatus === 'published' ? 'publiée' : 'en brouillon'}.` });
  };

  const handleAutoTranslate = () => {
    const sourceTitle = formData.title || formData.titleAr || formData.titleZgh;
    const sourceContent = formData.content || formData.contentAr || formData.contentZgh;

    if (!sourceTitle && !sourceContent) {
      toast({ title: "Aucun texte", description: "Veuillez saisir un texte dans au moins une langue.", variant: "destructive" });
      return;
    }

    setIsTranslating(true);
    
    // Dictionnaire pour les textes actuels
    const dict: Record<string, {ar: string, zgh: string}> = {
      "Soutien aux Jeunes Candidats": {
        ar: "دعم المرشحين الشباب",
        zgh: "ⴰⵏⵏⴰⵍ ⵉ ⵉⵎⵄⵔⴰⴹ ⵉⵄⵔⵔⵉⵎⵏ"
      },
      "Remboursement des dépenses de campagne jusqu'à 75% pour les jeunes de moins de 35 ans (Loi organique 04.21).": {
        ar: "تعويض مصاريف الحملة الانتخابية بنسبة تصل إلى 75٪ للشباب دون سن 35 عامًا.",
        zgh: "ⴰⵙⵔⵔⵓⵙ ⵏ ⵉⵙⵔⴰⴳⵏ ⵏ ⵜⵎⵙⴽⵉⵔⵜ ⴰⵔ 75% ⵉ ⵉⵄⵔⵔⵉⵎⵏ ⴷⴷⴰⵡ 35 ⵏ ⵓⵙⴳⴳⵯⴰⵙ (ⴰⵣⵔⴼ 04.21)."
      },
      "Révision des Listes Électorales": {
        ar: "مراجعة اللوائح الانتخابية",
        zgh: "ⴰⵙⵎⵓⵜⵜⴳ ⵏ ⵜⵍⴳⴰⵎⵉⵏ ⵏ ⵉⵙⵜⴰⵢⵏ"
      },
      "Décret du Ministre de l'Intérieur N° 690.26 (22 avril 2026) : délai fixé pour la révision des listes électorales en prévision des élections de septembre 2026.": {
        ar: "قرار لوزير الداخلية رقم 690.26 (22 أبريل 2026) بتحديد الآجال المتعلقة بمراجعة اللوائح الانتخابية تحضيرا لانتخابات شتنبر 2026.",
        zgh: "ⴰⴹⴰⵕ ⵏ ⵓⵎⴰⵡⵙⴰⵙ ⵏ ⵓⴳⵏⵙⵓ ⵓⵟⵟⵓⵏ 690.26 (22 ⵉⴱⵔⵉⵔ 2026) : ⴰⵙⴰⴽⵓⴷ ⵏ ⵓⵙⵎⵓⵜⵜⴳ ⵏ ⵜⵍⴳⴰⵎⵉⵏ."
      }
    };

    const t = (text: string, lang: 'ar'|'zgh') => {
      if (!text) return "";
      const cleaned = text.trim();
      if (cleaned.includes("Soutien aux Jeunes") || cleaned.includes("دعم المرشحين")) return dict["Soutien aux Jeunes Candidats"][lang];
      if (cleaned.includes("Remboursement des dépenses") || cleaned.includes("75%")) return dict["Remboursement des dépenses de campagne jusqu'à 75% pour les jeunes de moins de 35 ans (Loi organique 04.21)."][lang];
      if (cleaned.includes("Révision des Listes") || cleaned.includes("مراجعة اللوائح")) return dict["Révision des Listes Électorales"][lang];
      if (cleaned.includes("690.26") || cleaned.includes("délai fixé")) return dict["Décret du Ministre de l'Intérieur N° 690.26 (22 avril 2026) : délai fixé pour la révision des listes électorales en prévision des élections de septembre 2026."][lang];
      return lang === 'ar' ? `[مترجم] ${cleaned}` : `[ⵜⴰⵙⵓⵖⵍⵜ] ${cleaned}`;
    };

    // Simulation d'un appel à une API de traduction (ex: Google Translate ou IA)
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        titleAr: t(prev.title, 'ar'),
        titleZgh: t(prev.title, 'zgh'),
        contentAr: t(prev.content, 'ar'),
        contentZgh: t(prev.content, 'zgh'),
      }));
      setIsTranslating(false);
      toast({ title: "Traductions générées", description: "Les traductions ont été ajoutées. Veuillez les vérifier." });
    }, 1000);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-card p-6 rounded-2xl shadow-sm border border-border">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-primary" />
            Gestion des Spot News
          </h1>
          <p className="text-muted-foreground font-body">Gérez les actualités chaudes (Spot News) affichées en format bulles sur le site.</p>
        </div>
        <Button onClick={() => { setFormData(initialForm); setEditingId(null); setShowForm(!showForm); }} className="gap-2 font-semibold">
          {showForm ? <XCircle className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Annuler" : "Créer un Spot News"}
        </Button>
      </div>

      <Card className="border-border shadow-sm">
        <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-primary" /> Configuration Globale
          </CardTitle>
          <CardDescription>Paramètres d'affichage du bandeau Spot News sur la plateforme.</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="space-y-3 col-span-1 md:col-span-2">
              <Label>Titre du Bandeau (Tag)</Label>
              <Tabs defaultValue="fr" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-2">
                  <TabsTrigger value="fr">Français</TabsTrigger>
                  <TabsTrigger value="ar">Arabe</TabsTrigger>
                  <TabsTrigger value="zgh">Amazigh</TabsTrigger>
                </TabsList>
                <TabsContent value="fr">
                  <Input value={config.bannerTitle} onChange={e => setConfig({ ...config, bannerTitle: e.target.value })} placeholder="Ex: Skip pas!, Alerte, Flash..." />
                </TabsContent>
                <TabsContent value="ar">
                  <Input dir="rtl" value={config.bannerTitleAr || ''} onChange={e => setConfig({ ...config, bannerTitleAr: e.target.value })} placeholder="Ex: ماتنساش" />
                </TabsContent>
                <TabsContent value="zgh">
                  <Input value={config.bannerTitleZgh || ''} onChange={e => setConfig({ ...config, bannerTitleZgh: e.target.value })} placeholder="Ex: ⵓⵔ ⵜⵜⵓ" />
                </TabsContent>
              </Tabs>
              <p className="text-xs text-muted-foreground mt-1">Le texte affiché dans la petite bulle rouge à gauche de l'écran.</p>
            </div>
            
            <div className="space-y-3">
              <Label>Vitesse de défilement (secondes)</Label>
              <div className="flex items-center gap-3">
                <Input 
                  type="range" 
                  min="20" 
                  max="150" 
                  value={config.speed || 60} 
                  onChange={e => setConfig({ ...config, speed: parseInt(e.target.value) })} 
                  className="flex-1"
                />
                <span className="text-sm font-bold min-w-[3ch]">{config.speed || 60}s</span>
              </div>
              <p className="text-xs text-muted-foreground">Plus le nombre est grand, plus le texte défile lentement.</p>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-xl bg-muted/20 h-full">
              <div className="space-y-0.5">
                <Label className="text-base">Afficher le Bandeau</Label>
                <p className="text-xs text-muted-foreground">Active ou désactive l'apparition du bandeau sur la page d'accueil.</p>
              </div>
              <Switch 
                checked={config.isVisible} 
                onCheckedChange={(c) => setConfig({ ...config, isVisible: c })} 
              />
            </div>
          </div>
          <div className="flex justify-end mt-6 pt-4 border-t border-border">
            <Button onClick={handleSaveConfig} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
              <CheckCircle className="w-4 h-4" /> Enregistrer la configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <Card className="border-primary/20 shadow-lg animate-in fade-in slide-in-from-top-4">
          <CardHeader className="bg-muted/30 border-b border-border/50 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                {editingId ? "Modifier le Spot News" : "Nouveau Spot News"}
              </CardTitle>
              <CardDescription className="mt-1">Configurez le contenu et l'apparence de cette actualité (format bulle).</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2 border-primary/50 text-primary hover:bg-primary/10"
              onClick={handleAutoTranslate}
              disabled={isTranslating}
            >
              {isTranslating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-500" />}
              Traduire avec l'IA
            </Button>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <Tabs defaultValue="fr" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="fr">Français (Défaut)</TabsTrigger>
                <TabsTrigger value="ar">Arabe</TabsTrigger>
                <TabsTrigger value="zgh">Amazigh</TabsTrigger>
              </TabsList>
              
              <TabsContent value="fr" className="space-y-6 mt-0">
                <div className="space-y-3">
                  <Label>Titre de l'actualité</Label>
                  <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Nouveau débat ce soir..." className="font-bold text-lg" />
                </div>
                <div className="space-y-3">
                  <Label>Contenu / Résumé</Label>
                  <Textarea value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })} placeholder="Décrivez l'actualité brièvement..." className="min-h-[80px]" />
                </div>
              </TabsContent>
              
              <TabsContent value="ar" className="space-y-6 mt-0">
                <div className="space-y-3">
                  <Label>Titre de l'actualité (Arabe)</Label>
                  <Input dir="rtl" value={formData.titleAr || ''} onChange={e => setFormData({ ...formData, titleAr: e.target.value })} placeholder="العنوان" className="font-bold text-lg font-arabic" />
                </div>
                <div className="space-y-3">
                  <Label>Contenu / Résumé (Arabe)</Label>
                  <Textarea dir="rtl" value={formData.contentAr || ''} onChange={e => setFormData({ ...formData, contentAr: e.target.value })} placeholder="المحتوى" className="min-h-[80px] font-arabic" />
                </div>
              </TabsContent>
              
              <TabsContent value="zgh" className="space-y-6 mt-0">
                <div className="space-y-3">
                  <Label>Titre de l'actualité (Amazigh)</Label>
                  <Input value={formData.titleZgh || ''} onChange={e => setFormData({ ...formData, titleZgh: e.target.value })} placeholder="ⴰⵣⵡⵍ" className="font-bold text-lg font-amazigh" />
                </div>
                <div className="space-y-3">
                  <Label>Contenu / Résumé (Amazigh)</Label>
                  <Textarea value={formData.contentZgh || ''} onChange={e => setFormData({ ...formData, contentZgh: e.target.value })} placeholder="ⴰⴳⴱⵓⵔ" className="min-h-[80px] font-amazigh" />
                </div>
              </TabsContent>
            </Tabs>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label>Image d'illustration (Optionnel)</Label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/*" onChange={handleImageUpload} className="w-full" />
                  {formData.imageUrl && <Button variant="ghost" onClick={() => setFormData({...formData, imageUrl: ""})}><XCircle className="w-4 h-4 text-destructive"/></Button>}
                </div>
                {formData.imageUrl && (
                  <div className="mt-2 rounded-xl overflow-hidden border h-32 flex items-center justify-center bg-muted">
                    <img src={formData.imageUrl} className="h-full w-full object-cover" alt="Aperçu" />
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <Label>Lien Hypertexte (Optionnel)</Label>
                <Input value={formData.linkUrl || ''} onChange={e => setFormData({ ...formData, linkUrl: e.target.value })} placeholder="https://..." />
                <p className="text-xs text-muted-foreground">Où l'utilisateur sera redirigé s'il clique sur la bulle.</p>
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-border">
              <Button variant="outline" onClick={() => handleSave("draft")}>Sauvegarder en Brouillon</Button>
              <Button onClick={() => handleSave("published")}>{editingId ? "Mettre à jour & Publier" : "Publier immédiatement"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des spot news en format bulles */}
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mt-6">
        {newsList.length === 0 && !showForm ? (
          <div className="col-span-full text-center py-20 bg-card rounded-2xl border border-border">
            <Newspaper className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-xl font-display font-medium text-foreground">Aucune actualité Spot News</h3>
            <p className="text-muted-foreground mt-2">Créez votre première bulle d'actualité pour informer vos utilisateurs.</p>
          </div>
        ) : (
          newsList.map(news => (
            <Card key={news.id} className={`transition-all hover:shadow-md ${news.status === 'draft' ? "opacity-75 bg-muted/20 border-dashed" : "border-primary/20 shadow-sm"} flex flex-col`}>
              {news.imageUrl && (
                <div className="h-40 w-full overflow-hidden rounded-t-xl">
                  <img src={news.imageUrl} alt={news.title} className="w-full h-full object-cover transition-transform hover:scale-105" />
                </div>
              )}
              <CardContent className={`p-5 flex-1 flex flex-col ${!news.imageUrl && 'pt-6'}`}>
                <div className="flex justify-between items-start mb-2 gap-2">
                  <h3 className="font-display font-bold text-lg leading-tight line-clamp-2">{news.title}</h3>
                  <Badge variant={news.status === 'published' ? 'default' : 'secondary'} className="whitespace-nowrap">
                    {news.status === 'published' ? 'Publié' : 'Brouillon'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground font-body line-clamp-3 mb-4 flex-1">
                  {news.content}
                </p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-border/50">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={news.status === 'published'} 
                      onCheckedChange={() => toggleStatus(news.id, news.status)} 
                    />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {news.status === 'published' ? 'Actif' : 'Caché'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(news)} className="text-blue-500 hover:text-blue-600 hover:bg-blue-50/50 h-8 w-8">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(news.id)} className="text-destructive hover:bg-destructive/10 h-8 w-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminSpotNewsPage;
